import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  facebookAPI,
  handleApiError,
  type DigestCity,
  type FacebookAutopostState,
  type PagePost,
  type PagePostStatus,
  type SocialGroup,
  type SocialGroupPayload,
} from '../services/api';
import './Dashboard.css';
import './Maintenance.css';
import './Facebook.css';

const STATUS_LABELS: Record<PagePostStatus, string> = {
  scheduled: 'Насрочена',
  posted: 'Публикувана',
  failed: 'Неуспешна',
  skipped: 'Пропусната',
  deleted: 'Изтрита',
};

const formatTime = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('bg-BG');
};

const todayLocal = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const ErrorLine: React.FC<{ message: string | null }> = ({ message }) =>
  message ? (
    <div className="maintenance-error">
      <i className="fas fa-exclamation-triangle"></i>
      <span>{message}</span>
    </div>
  ) : null;

// Section 1: the Page autopost switch and the log of recent Page posts.
const AutopostSection: React.FC = () => {
  const [state, setState] = useState<FacebookAutopostState | null>(null);
  const [posts, setPosts] = useState<PagePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await facebookAPI.getAutopost();
        setState(data);
      } catch (err) {
        setError(handleApiError(err));
      }
      try {
        const { data } = await facebookAPI.getPagePosts(50);
        setPosts(data.items);
      } catch (err) {
        setError(handleApiError(err));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleToggle = async () => {
    if (!state) return;
    setSaving(true);
    setError(null);
    try {
      const { data } = await facebookAPI.setAutopost({ enabled: !state.enabled });
      setState(data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="maintenance-messages fb-section">
      <h3>Автоматични публикации във Facebook страницата</h3>
      <ErrorLine message={error} />

      {state && !state.page_configured && (
        <div className="fb-warning">
          <i className="fas fa-exclamation-triangle"></i> Токенът на страницата още не е
          зададен. Превключвателят няма ефект.
        </div>
      )}

      <div className={`maintenance-status ${state?.enabled ? 'on' : 'off'}`}>
        <div className="maintenance-status-icon">
          <i className={state?.enabled ? 'fas fa-check-circle' : 'fas fa-power-off'}></i>
        </div>
        <div className="maintenance-status-text">
          <div className="maintenance-status-title">
            {loading
              ? 'Зареждане…'
              : !state
                ? 'Състоянието не е известно'
                : state.enabled
                  ? 'Автоматичните публикации са ВКЛЮЧЕНИ'
                  : 'Автоматичните публикации са изключени'}
          </div>
          {state && (
            <div className="maintenance-status-sub">
              <div>
                {state.page_configured
                  ? 'Страницата е свързана'
                  : 'Страницата не е свързана'}
              </div>
              <div>
                {state.posts_last_hour} от {state.hourly_cap} публикации през последния час
              </div>
            </div>
          )}
        </div>
        <button
          className={`maintenance-toggle ${state?.enabled ? 'danger' : 'primary'}`}
          onClick={handleToggle}
          disabled={loading || saving || !state}
        >
          {saving ? 'Запазване…' : state?.enabled ? 'Изключи публикациите' : 'Включи публикациите'}
        </button>
      </div>

      <h4 className="fb-subtitle">Последни публикации в страницата</h4>
      {posts.length === 0 ? (
        <p className="maintenance-hint">{loading ? 'Зареждане…' : 'Още няма публикации.'}</p>
      ) : (
        <table className="fb-table">
          <thead>
            <tr>
              <th>Време</th>
              <th>Пътуване</th>
              <th>Статус</th>
              <th>Публикация</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id}>
                <td>{formatTime(p.posted_at || p.created_at)}</td>
                <td>{p.title || 'Без заглавие'}</td>
                <td>
                  <span className={`fb-status fb-status-${p.status}`}>
                    {STATUS_LABELS[p.status] ?? p.status}
                  </span>
                  {p.status === 'failed' && p.error && (
                    <div className="fb-post-error">{p.error}</div>
                  )}
                </td>
                <td>
                  {p.external_id && (
                    <a
                      href={`https://www.facebook.com/${p.external_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Виж във Facebook
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

interface CityCardProps {
  date: string;
  city: DigestCity;
}

// One city of the daily digest: ready text, search link, groups with a posted mark.
const CityCard: React.FC<CityCardProps> = ({ date, city }) => {
  const [groups, setGroups] = useState(city.groups);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copyNote, setCopyNote] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(city.text);
      setCopyNote('Копирано');
    } catch {
      // The clipboard can be blocked. Select the text so Ctrl+C works.
      textRef.current?.focus();
      textRef.current?.select();
      setCopyNote('Не успях да копирам. Текстът е маркиран, копирай го ръчно.');
    }
  };

  const handlePosted = async (groupId: string, posted: boolean) => {
    setBusy(groupId);
    setError(null);
    try {
      const { data } = await facebookAPI.setPosted({
        date,
        city: city.city,
        group_id: groupId,
        posted,
      });
      setGroups((prev) =>
        prev.map((g) =>
          g.id === groupId ? { ...g, posted: data.posted, posted_at: data.posted_at } : g
        )
      );
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fb-city-card" data-testid="fb-city-card">
      <div className="fb-city-head">
        <h4>{city.city}</h4>
        <span className="fb-count">{city.ride_count} пътувания</span>
      </div>
      <textarea
        ref={textRef}
        className="fb-digest-text"
        readOnly
        rows={8}
        value={city.text}
        aria-label={`Текст за ${city.city}`}
      />
      <div className="fb-actions">
        <button className="maintenance-toggle primary" onClick={handleCopy}>
          Копирай текста
        </button>
        <a href={city.search_url} target="_blank" rel="noopener noreferrer">
          Търси в групите
        </a>
        {copyNote && <span className="fb-copy-note">{copyNote}</span>}
      </div>
      <ErrorLine message={error} />
      {groups.length === 0 ? (
        <p className="maintenance-hint">Няма групи за този град.</p>
      ) : (
        <ul className="fb-group-list">
          {groups.map((g) => (
            <li key={g.id}>
              <span className="fb-group-name">{g.name}</span>
              <a href={g.url} target="_blank" rel="noopener noreferrer">
                Отвори групата
              </a>
              <label className="fb-posted">
                <input
                  type="checkbox"
                  checked={g.posted}
                  disabled={busy === g.id}
                  onChange={(e) => handlePosted(g.id, e.target.checked)}
                  aria-label={`Публикувано в ${g.name}`}
                />
                Публикувано
              </label>
              {g.posted && g.posted_at && (
                <span className="fb-posted-at">{formatTime(g.posted_at)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Section 2: the daily digest for the groups.
const DigestSection: React.FC = () => {
  const [date, setDate] = useState<string>(todayLocal());
  const [cities, setCities] = useState<DigestCity[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCities(null);
    setError(null);
    facebookAPI
      .getDigest(date)
      .then(({ data }) => {
        if (!cancelled) setCities(data.cities);
      })
      .catch((err) => {
        if (!cancelled) setError(handleApiError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [date]);

  return (
    <div className="maintenance-messages fb-section">
      <h3>Публикации за групите (днес)</h3>
      <label className="fb-date">
        Дата
        <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
      </label>
      <ErrorLine message={error} />
      {cities === null && !error && <p className="maintenance-hint">Зареждане…</p>}
      {cities !== null && cities.length === 0 && (
        <p className="maintenance-hint">Няма пътувания за тази дата в нито един град.</p>
      )}
      {cities?.map((c) => (
        // Key by date too, so the posted marks reset when the date changes.
        <CityCard key={`${date}:${c.city}`} date={date} city={c} />
      ))}
    </div>
  );
};

interface GroupFormState {
  name: string;
  url: string;
  city: string;
  active: boolean;
  notes: string;
}

const EMPTY_FORM: GroupFormState = { name: '', url: '', city: '', active: true, notes: '' };

// Section 3: the group list and the add or edit form.
const GroupsSection: React.FC = () => {
  const [groups, setGroups] = useState<SocialGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<GroupFormState>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await facebookAPI.getGroups();
      setGroups(data.items);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (g: SocialGroup) => {
    setEditingId(g.id);
    setError(null);
    setForm({ name: g.name, url: g.url, city: g.city ?? '', active: g.active, notes: g.notes ?? '' });
  };

  const reset = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: SocialGroupPayload = {
      name: form.name.trim(),
      url: form.url.trim(),
      city: form.city.trim() || null,
      active: form.active,
      notes: form.notes.trim() || null,
    };
    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        await facebookAPI.updateGroup(editingId, payload);
      } else {
        await facebookAPI.createGroup(payload);
      }
      reset();
      await load();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="maintenance-messages fb-section">
      <h3>Facebook групи</h3>
      {loading ? (
        <p className="maintenance-hint">Зареждане…</p>
      ) : groups.length === 0 ? (
        <p className="maintenance-hint">Още няма добавени групи.</p>
      ) : (
        <table className="fb-table">
          <thead>
            <tr>
              <th>Име</th>
              <th>Град</th>
              <th>Активна</th>
              <th>Бележки</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.id} className={g.active ? '' : 'fb-inactive'}>
                <td>{g.name}</td>
                <td>{g.city || 'Всички градове'}</td>
                <td>{g.active ? 'Да' : 'Не'}</td>
                <td>{g.notes}</td>
                <td>
                  <button
                    className="fb-link-button"
                    onClick={() => startEdit(g)}
                    aria-label={`Редактирай ${g.name}`}
                  >
                    Редактирай
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <form className="fb-form" onSubmit={handleSubmit}>
        <h4 className="fb-subtitle">{editingId ? 'Редакция на група' : 'Нова група'}</h4>
        <ErrorLine message={error} />
        <label className="maintenance-field">
          <span>Име</span>
          <input
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </label>
        <label className="maintenance-field">
          <span>Адрес (https://www.facebook.com/groups/...)</span>
          <input
            type="url"
            required
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
          />
        </label>
        <label className="maintenance-field">
          <span>Град (празно за всички градове)</span>
          <input
            type="text"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
          />
        </label>
        <label className="maintenance-field">
          <span>Бележки</span>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </label>
        <label className="fb-posted">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
          />
          Активна
        </label>
        <div className="fb-actions">
          <button type="submit" className="maintenance-toggle primary" disabled={saving}>
            {saving ? 'Запазване…' : editingId ? 'Запази промените' : 'Добави групата'}
          </button>
          {editingId && (
            <button type="button" className="fb-link-button" onClick={reset}>
              Отказ
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

const Facebook: React.FC = () => (
  <div className="dashboard">
    <div className="dashboard-header">
      <div className="header-content">
        <h2>Facebook</h2>
        <p className="last-updated">
          Страницата получава публикации автоматично. В групите публикациите се поставят на ръка.
        </p>
      </div>
    </div>
    <AutopostSection />
    <DigestSection />
    <GroupsSection />
  </div>
);

export default Facebook;
